import { preventFormSubmit } from '@helpers/@utils/prevent-form-submit';
import React, { useImperativeHandle } from 'react';
import { FieldErrors, FieldValues, FormProvider, UseFormProps, UseFormReturn } from 'react-hook-form';
import useExtendedForm from './hooks/useExtendedForm';
import TriggerFormInitially from './TriggerFormInitially';

export type SubmitHandler<TFieldValues extends FieldValues = FieldValues, TContext = AnyValue> = (
  data: TFieldValues,
  event?: React.BaseSyntheticEvent,
  args?: UseFormReturn<TFieldValues, TContext>
) => AnyValue | Promise<AnyValue>;

export type SubmitErrorHandler<TFieldValues extends FieldValues = FieldValues, TContext = AnyValue> = (
  data: FieldErrors<TFieldValues>,
  event?: React.BaseSyntheticEvent,
  args?: UseFormReturn<TFieldValues, TContext>
) => AnyValue | Promise<AnyValue>;

interface FormHookWrapperNoFooterProps<TFieldValues extends FieldValues = FieldValues, TContext = AnyValue>
  extends UseFormProps<TFieldValues, TContext> {
  validateInitially?: boolean;
  validationSchema?: AnyValue | (() => AnyValue);
  preventEnterSubmit?: boolean;
  onSubmit: SubmitHandler<TFieldValues, TContext>;
  onError?: SubmitErrorHandler<TFieldValues> | undefined;
  children?: (args: UseFormReturn<TFieldValues, TContext>) => React.ReactNode;
}

export type FormHookWrapperNoFooterRef<TFieldValues extends FieldValues = FieldValues, TContext = AnyValue> = UseFormReturn<
  TFieldValues,
  TContext
>;

function FormHookWrapperNoFooterInner<TFieldValues extends FieldValues = FieldValues, TContext = AnyValue>(
  {
    children,
    onSubmit,
    onError,
    validateInitially,
    validationSchema,
    preventEnterSubmit = true,
    ...props
  }: FormHookWrapperNoFooterProps<TFieldValues, TContext>,
  forwardRef: React.ForwardedRef<UseFormReturn<TFieldValues, TContext>>
): JSX.Element {
  const args = useExtendedForm<TFieldValues, TContext>({
    validationSchema,
    ...props,
  });

  const onFormSubmit = (data: TFieldValues, event?: React.BaseSyntheticEvent) => {
    return onSubmit(data, event, args);
  };

  const onFormError = (data: FieldErrors<TFieldValues>, event?: React.BaseSyntheticEvent) => {
    return onError && onError(data, event, args);
  };

  useImperativeHandle(forwardRef, () => ({
    ...args,
    submit: handleManualSubmit,
  }));

  const handleManualSubmit = (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    return args.handleSubmit(onFormSubmit, onFormError)();
  };

  return (
    <FormProvider {...args}>
      <form onKeyDown={preventEnterSubmit ? preventFormSubmit : undefined} onSubmit={handleManualSubmit}>
        {children && children(args)}
      </form>
      {validateInitially && <TriggerFormInitially {...args} />}
    </FormProvider>
  );
}

const FormHookWrapperNoFooter = React.forwardRef(FormHookWrapperNoFooterInner) as <
  TFieldValues extends FieldValues = FieldValues,
  TContext = AnyValue
>(
  props: FormHookWrapperNoFooterProps<TFieldValues, TContext> & {
    ref?: React.ForwardedRef<FormHookWrapperNoFooterRef<TFieldValues, TContext>>;
  }
) => ReturnType<typeof FormHookWrapperNoFooterInner>;

export default FormHookWrapperNoFooter;
